'use client'

import Image from 'next/image'
import Link from 'next/link'
import { motion } from 'motion/react'

import { typographyVariants } from '@ui/web/lib'
import { buildUrl } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { poppins } from '~/font'
import kidImage from './images/kid.webp'
import parentImage from './images/parent.webp'
import teacherImage from './images/teacher.webp'

const USER_CONFIG = {
  kid: {
    image: kidImage,
    label: 'Anak',
    signup: buildUrl('account', '/anak'),
    signin: buildUrl('account', '/anak'),
  },
  parent: {
    image: parentImage,
    label: 'Orang Tua',
    signup: buildUrl('account', '/daftar', { signup_type: 'parent' }),
    signin: buildUrl('account', '/masuk'),
  },
  teacher: {
    image: teacherImage,
    label: 'Guru',
    signup: buildUrl('account', '/daftar', { signup_type: 'teacher' }),
    signin: buildUrl('account', '/masuk'),
  },
} as const

type UserType = keyof typeof USER_CONFIG
type UserNavigationMode = 'signin' | 'signup'

const BUTTON_VARIANTS = {
  initial: {
    scale: 1,
    backgroundColor: 'transparent',
    borderColor: 'var(--blue-50)',
    color: 'var(--neutral-black)',
  },
  hover: {
    scale: 1.05,
    backgroundColor: 'var(--blue-10)',
    borderColor: 'transparent',
    color: 'var(--blue-80)',
  },
  tap: {
    scale: 0.95,
    backgroundColor: 'var(--blue-10)',
    borderColor: 'transparent',
    color: 'var(--blue-80)',
  },
} as const

const BUTTON_TRANSITION = {
  duration: 0.2,
  ease: 'easeInOut',
} as const

interface UserNavigationButtonProps {
  userType: UserType
  mode: UserNavigationMode
  className?: string
  disabled?: boolean
}

export function UserNavigationButton({ userType, mode, className, disabled }: UserNavigationButtonProps) {
  const { image, label, [mode]: href } = USER_CONFIG[userType]

  const buttonContent = (
    <motion.div
      className={cn(
        poppins.className,
        'flex size-36 flex-col items-center gap-2 rounded-3xl border-2 border-solid p-4',
        disabled ? 'cursor-default opacity-50' : 'cursor-pointer',
        className,
      )}
      variants={BUTTON_VARIANTS}
      initial='initial'
      whileHover={disabled ? undefined : 'hover'}
      whileTap={disabled ? undefined : 'tap'}
      transition={BUTTON_TRANSITION}
    >
      <Image
        src={image}
        alt={label}
        width={72}
        height={72}
        draggable={false}
        className='size-18 rounded-full object-cover select-none'
      />

      <span className={cn(typographyVariants({ variant: 'title-lg' }))}>{label}</span>
    </motion.div>
  )

  if (disabled) {
    return (
      <span aria-disabled='true' role='link'>
        {buttonContent}
      </span>
    )
  }

  return (
    <Link href={href} className='h-fit w-fit'>
      {buttonContent}
    </Link>
  )
}
